import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Alert, Chip, IconButton, Stack, Typography } from '@mui/material';
import MotiveChart from 'features/augmented-writing/analysis-results/motive-chart';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { TestContactPreview, TestMessagePreview } from '../../campaign-builder.types';

/** Zeichen wie beim Versand gezählt (Unicode-Codepoints, Emojis zählen einfach). */
const countCharacters = (text: string) => Array.from(text || '').length;

interface Props {
  testContacts: TestContactPreview[];
  activeTestContactIndex: number;
  activeMessageIndex: number;

  handleNextContact(): void;

  handlePrevContact(): void;

  handleNextMessage(): void;

  handlePrevMessage(): void;
}

const MessagePreviewCarousel: React.FC<Props> = ({
  testContacts,
  activeTestContactIndex,
  activeMessageIndex,
  handleNextContact,
  handlePrevContact,
  handleNextMessage,
  handlePrevMessage,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const testContact = useMemo(() => testContacts[activeTestContactIndex], [testContacts, activeTestContactIndex]);
  const totalContacts = testContacts?.length;

  const messages = testContact?.messages;
  const totalMessages = messages?.length;
  const message: TestMessagePreview | undefined = !_.isNil(messages?.[activeMessageIndex]) ? messages?.[activeMessageIndex] : undefined;
  const skipped = testContact?.skipped ?? [];
  const note = message?.connectionNote;
  const characters = note?.characters ?? countCharacters(message?.text ?? '');

  return (
    <div className={classes.messagePreviewCarousel}>
      <Stack className={classes.contactCarousel} direction={'row'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={3}>
          {totalContacts > 1 ? (
            <IconButton className={classes.iconButton} onClick={handlePrevContact}>
              <NavigateBeforeIcon />
            </IconButton>
          ) : null}
          <Stack direction={'row'} alignItems={'center'} gap={3}>
            <div>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <div className={cx(classes.circle, 'blue')} />
                <Typography className={classes.blueText}>{t('yourMessage')}</Typography>
              </Stack>
              <Stack direction={'row'} alignItems={'center'} gap={1} mb={1}>
                <div className={cx(classes.circle, 'orange')} />
                <Typography className={classes.orangeText}>{testContact?.contact?.assessment}</Typography>
              </Stack>
              <Typography className={classes.text}>{testContact?.contact?.name}</Typography>
              <Typography className={classes.text}>{`${t('contact')} ${activeTestContactIndex + 1} / ${totalContacts}`}</Typography>
            </div>
          </Stack>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={3}>
          <MotiveChart userScores={message?.percentiles.text} contactScores={message?.percentiles.profile} />
          {totalContacts > 1 ? (
            <IconButton className={classes.iconButton} onClick={handleNextContact}>
              <NavigateNextIcon />
            </IconButton>
          ) : null}
        </Stack>
      </Stack>
      {!_.isNil(message) ? (
        <div className={classes.messageCarousel}>
          <Stack direction={'row'} alignItems={'center'} gap={2} mb={2}>
            <IconButton className={classes.iconButtonSmall} onClick={handlePrevMessage}>
              <ArrowBackIcon />
            </IconButton>
            <Typography className={classes.boldText}>
              {`${t('message')} ${message.number ?? activeMessageIndex + 1}: ${t(message.functionName)}`}
              <span className={classes.position}>{` (${activeMessageIndex + 1} / ${totalMessages})`}</span>
            </Typography>
            <IconButton className={classes.iconButtonSmall} onClick={handleNextMessage}>
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
          <div className={classes.messageContent}>
            {message.guidanceUsed ? (
              <Alert severity={'warning'} className={classes.notice}>
                {t('testGuidanceInsteadOfMessage')}
              </Alert>
            ) : null}
            {!_.isNil(message.subject) ? (
              <div className={classes.subjectContainer}>
                <Typography className={classes.blockText}>{`${t('subject')}: ${message.subject}`}</Typography>
              </div>
            ) : null}
            <div className={classes.messageContainer}>
              <Typography className={classes.blockText}>{message.text}</Typography>
            </div>
            {message.guidanceUsed && message.suggestedPrompt ? (
              <div className={classes.subjectContainer}>
                <Typography className={classes.boldText}>{t('testSuggestedInstruction')}</Typography>
                <Typography className={classes.blockText}>{message.suggestedPrompt}</Typography>
              </div>
            ) : null}
            {!message.guidanceUsed ? (
              <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'} className={classes.meta}>
                <Typography className={classes.metaText}>
                  {note ? `${characters} / ${note.limit} ${t('testCharacters')}` : `${characters} ${t('testCharacters')}`}
                </Typography>
                {note?.outcome === 'shortened' ? (
                  <Chip size={'small'} label={t('testNoteShortened', { before: note.originalCharacters })} />
                ) : null}
                {note?.outcome === 'regenerated' ? (
                  <Chip size={'small'} label={t('testNoteRegenerated', { before: note.originalCharacters })} />
                ) : null}
              </Stack>
            ) : null}
            {note?.outcome === 'too_long' ? (
              <Alert severity={'error'} className={classes.notice}>
                {t('testNoteTooLong', { characters: note.characters, limit: note.limit })}
              </Alert>
            ) : null}
          </div>
        </div>
      ) : null}
      {!_.isEmpty(skipped) ? (
        <Alert severity={'info'} className={classes.skipped}>
          <Typography className={classes.boldText}>{t('testSkippedSteps')}</Typography>
          {skipped.map((skip) => (
            <Typography key={`${skip.stepIndex}-${skip.functionName}`} className={classes.metaText}>
              {`${t('message')} ${skip.number ?? skip.stepIndex + 1} (${t(skip.functionName)}): ${skip.reason}`}
            </Typography>
          ))}
        </Alert>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  messagePreviewCarousel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  iconButton: {
    color: '#FFF',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(24),
    },
    '&:hover': {
      background: 'linear-gradient(to left, #5044FF, #4AA4FF)',
    },
  },
  iconButtonSmall: {
    color: theme.palette.text.primary,
    padding: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  blueText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  orangeText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.analysisSecondary.main,
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
  },
  boldText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  blockText: {
    fontSize: theme.typography.pxToRem(16),
    whiteSpace: 'pre-wrap',
  },
  contactCarousel: {
    width: '100%',
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    flexShrink: 0,
  },
  messageCarousel: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: '8px',
  },
  subjectContainer: {
    background: theme.palette.backgrounds.main_new,
    borderRadius: '12px',
    padding: '8px 16px',
    flexShrink: 0,
  },
  messageContainer: {
    background: theme.palette.backgrounds.main_new,
    borderRadius: '12px',
    padding: '16px',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  position: {
    fontWeight: 'normal',
    color: theme.palette.greys.medium,
  },
  meta: {
    flexShrink: 0,
  },
  metaText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  notice: {
    flexShrink: 0,
    borderRadius: '12px',
  },
  skipped: {
    margin: '0 24px 16px 24px',
    borderRadius: '12px',
    flexShrink: 0,
  },
  circle: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    '&.blue': {
      background: theme.palette.primary.main,
    },
    '&.orange': {
      background: theme.palette.analysisSecondary.main,
    },
  },
}));

export default MessagePreviewCarousel;
